import React from "react";
import { BaseLayout } from "@layouts/BaseLayout";

import PageHero from "@shared/InnerPageHero";
import GetMeta from "@pages/SeoPages/GetMeta";

const Privacy = () => {
  return (
    <BaseLayout>
      <GetMeta
        description="Read our Privacy Policy to understand how we collect, use, and protect your personal information when you use Krent. Your privacy is our top priority."
        title={`Privacy | Krent Inc`}
        canonicalValue={"privacy"}
      />
      <section className="mb-5" style={{ marginTop: "-2rem" }}>
        <PageHero img="/assets/privacy.jpeg" currentPage="Privacy" />
      </section>
      <div className="container-fluid  mb-5">
        <div className="container">
          <div className="text-center mb-5">
            <h1 className="fw-bolder ">Privacy</h1>
            <div className="d-flex justify-content-center row">
              <p className=" col-md-9 col-lg-7">
                This privacy policy for Krent (“Company,” “we,” “us,” or “our”),
                describes how and why we might collect, store, use, and/or share
                (“process”) your information when you use our services
                (“Services”), such as when you:
                <br />
                1. Visit our website at https://krent.space, or any website of
                ours that links to this privacy notice;
                <br />
                2. Download and use our application(s), such as our mobile
                application or any other application of ours that links to this
                privacy policy;
                <br />
                3. Engage with us in other related ways.
              </p>
            </div>
          </div>
        </div>
      </div>
      <section className="mb-5 pb-5">
        <div className="container">
          <div className="  d-flex flex-column  justify-content-center col-md-9 mx-auto">
            <h4 className="fw-bold">QUESTIONS OR CONCERNS </h4>
            <p>
              Reading this privacy policy will help you understand your privacy
              rights and choices. If you do not agree with our policies and
              practices, please do not use our Services. If you still have any
              questions or concerns, please contact us at hello@krent.space.
            </p>
            <div>
              <h4 className="fw-bold">SUMMARY OF KEY POINTS</h4>
              <p>
                This summary provides key points from our privacy policy, but
                you can find out more details about any of these topics by using
                our table of contents below to find the section you are looking
                for.
              </p>
              <ol>
                <li>
                  <strong>What personal information do we process?</strong> When
                  you visit, use, or navigate our Services, we may process
                  personal information depending on how you interact with Krent
                  and the Services, the choices you make, and the features you
                  use.
                </li>
                <li>
                  <strong>
                    Do we process any sensitive personal information?
                  </strong>{" "}
                  We do not process sensitive personal information. However, we
                  may process sensitive personal information, when necessary,
                  with your consent or as otherwise permitted by applicable law.
                </li>
                <li>
                  <strong>
                    Do you receive any information from third parties?
                  </strong>{" "}
                  We may receive information from public databases, marketing
                  partners, social media platforms, and other outside sources.
                </li>
                <li>
                  <strong>How do you process my information?</strong> We process
                  your information to provide, improve, and administer our
                  Services, communicate with you, for security and fraud
                  prevention, and to comply with applicable laws and
                  regulations. We may also process your information for other
                  purposes with your consent. We process your information only
                  when we have a valid legal reason to do so.
                </li>
                <li>
                  <strong>How do we keep your information safe?</strong> We have
                  organizational and technical processes and procedures in place
                  to protect your personal information.
                </li>
              </ol>
            </div>
            <div>
              <p className="fw-bold">
                WANT TO LEARN MORE ABOUT WHAT KRENT CARE DOES WITH ANY
                INFORMATION WE COLLECT? REVIEW THE PRIVACY POLICY IN FULL BELOW.
              </p>
              <h4 className="fw-bold">TABLE OF CONTENTS</h4>
              <ol>
                <li>What information do we collect?</li>
                <li>How do we process your information?</li>
                <li>
                  What legal bases do we rely on to process your information?
                </li>
                <li>
                  When and with whom do we share your personal information?
                </li>
                <li>What is our stance on third-party websites?</li>
                <li>Do we use cookies and other tracking technologies?</li>
                <li>How long do we keep your information?</li>
                <li>How do we keep your information safe?</li>
                <li>Do we collect information from minors?</li>
                <li>What are your privacy rights?</li>
                <li>Controls for do-not-track features</li>
                <li>Do we make updates to this notice?</li>
              </ol>
            </div>
            <div>
              <ol>
                <li>
                  <h4>What information do we collect?</h4>
                  <p>
                    <strong>Personal Information Provided by You:</strong> The
                    personal information that we collect depends on the context
                    of your interactions with us and the Services, the choices
                    you make, and the features you use.
                  </p>
                  <p>
                    <strong>Payment Data:</strong> We may collect the necessary
                    data to process your payment if you make purchases, such as
                    your payment instrument number (such as a credit card
                    number), and the security code associated with your payment
                    instrument. All payment data is stored by Paystack. You may
                    find their privacy notice here:{" "}
                    <a
                      href="https://paystack.com/terms#dispute-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      https://paystack.com/terms#dispute-policy
                    </a>
                  </p>
                  <p>
                    <strong>Application Data:</strong> If you use our
                    application(s), we also may collect the following
                    information if you choose to provide us with access or
                    permission:
                  </p>
                  <ul>
                    <li>
                      <strong>Geolocation Information:</strong> We may request
                      access or permission to track location-based information
                      from your mobile device, either continuously or while you
                      are using our mobile application(s). You can change access
                      in your device’s settings.
                    </li>
                    <li>
                      <strong>Mobile Device Access:</strong> We may request
                      access to certain features such as Bluetooth, calendar,
                      camera, and others. You can change this in your device’s
                      settings.
                    </li>
                    <li>
                      <strong>Mobile Device Data:</strong> We automatically
                      collect device information such as your mobile device ID,
                      model, OS version, system configuration, IP address, and
                      app usage data.
                    </li>
                    <li>
                      <strong>Push Notifications:</strong> We may request to
                      send you push notifications. You can disable these in your
                      device’s settings.
                    </li>
                  </ul>
                  <p>
                    This information is primarily needed to maintain the
                    security and operation of our applications, for
                    troubleshooting, and for internal analytics.
                  </p>
                  <p>
                    All personal information you provide must be true, complete,
                    and accurate, and you must notify us of any changes.
                  </p>

                  <p>
                    <strong>Information automatically collected:</strong> We
                    collect device and usage information such as IP address,
                    browser/device type, OS, language, referring URLs, country,
                    and usage data. This is needed for security, operation,
                    analytics, and reporting.
                  </p>
                  <ul>
                    <li>
                      <strong>Log and Usage Data:</strong> Service-related,
                      diagnostic, usage, and performance information recorded in
                      log files.
                    </li>
                    <li>
                      <strong>Device Data:</strong> Information about the
                      devices you use, such as hardware model, OS, browser type,
                      and carrier.
                    </li>
                    <li>
                      <strong>Location Data:</strong> Precise or imprecise
                      location data, depending on your device settings. You can
                      opt out by disabling location services.
                    </li>
                  </ul>

                  <p>
                    <strong>Information collected from other sources:</strong>{" "}
                    We may obtain information from public databases, marketing
                    partners, affiliates, social media platforms, and other
                    third parties. This may include mailing addresses, emails,
                    job titles, IP addresses, and social profiles.
                  </p>
                </li>

                <li>
                  <h4>How do we process your information?</h4>
                  <ul>
                    <li>To facilitate account creation and management.</li>
                    <li>To deliver and facilitate requested services.</li>
                    <li>To respond to inquiries and provide support.</li>
                    <li>
                      To send administrative information about our services.
                    </li>
                    <li>
                      To fulfill and manage orders, payments, and returns.
                    </li>
                    <li>To enable user-to-user communications.</li>
                    <li>To request feedback.</li>
                    <li>
                      To send marketing and promotional communications (opt-out
                      available).
                    </li>
                    <li>
                      To deliver targeted advertising (see our Cookie Notice:{" "}
                      <a
                        href="https://krent.space"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        https://krent.space
                      </a>
                      ).
                    </li>
                    <li>
                      To protect our Services (including fraud monitoring).
                    </li>
                    <li>To identify usage trends for improvements.</li>
                    <li>To measure marketing campaign effectiveness.</li>
                    <li>To save or protect vital interests.</li>
                  </ul>
                </li>

                <li>
                  <h4>
                    What legal bases do we rely on to process your information?
                  </h4>
                  <p>
                    We process your personal information when necessary under
                    applicable law, such as with your consent, to comply with
                    laws, fulfill contractual obligations, protect your rights,
                    or serve legitimate business interests.
                  </p>
                  <p>
                    In some exceptional cases, we may process information
                    without your consent, for example:
                  </p>
                  <ul>
                    <li>
                      If collection is clearly in your interest and consent
                      cannot be obtained in time.
                    </li>
                    <li>
                      For investigations, fraud detection, or business
                      transactions under certain conditions.
                    </li>
                    <li>
                      If required to comply with legal obligations like
                      subpoenas or court orders.
                    </li>
                  </ul>
                </li>

                <li>
                  <h4>
                    When and with whom do we share your personal information?
                  </h4>
                  <p>
                    We share data with trusted third-party vendors, service
                    providers, contractors, or agents who perform services for
                    us and require access to such data. These parties are bound
                    by contract to protect and only use the data as instructed.
                  </p>
                </li>

                <li>
                  <h4>What is our stance on third-party websites?</h4>
                  <p>
                    Our Services may link to third-party websites or display ads
                    from third parties. We are not responsible for their
                    content, practices, or policies. You should review their
                    policies before providing personal data.
                  </p>
                </li>

                <li>
                  <h4>Do we use cookies and other tracking technologies?</h4>
                  <p>
                    We may use cookies and similar tracking technologies. See
                    our Cookie Notice:{" "}
                    <a
                      href="https://krent.space"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      https://krent.space
                    </a>
                  </p>
                </li>

                <li>
                  <h4>How long do we keep your information?</h4>
                  <p>
                    We keep your information only as long as necessary for the
                    purposes outlined, unless longer retention is required by
                    law. When no longer needed, we delete or anonymize it, or
                    securely store it until deletion is possible.
                  </p>
                </li>

                <li>
                  <h4>How do we keep your information safe?</h4>
                  <p>
                    We have implemented appropriate technical and organizational
                    measures to protect your personal information. Access should
                    be from a secure environment.
                  </p>
                </li>

                <li>
                  <h4>Do we collect information from minors?</h4>
                  <p>
                    We do not knowingly collect data from anyone under 18. If we
                    discover such data, we delete it promptly. Contact us at{" "}
                    <a href="mailto:hello@krent.space">hello@krent.space</a> if
                    you believe we have collected such information.
                  </p>
                </li>

                <li>
                  <h4>What are your privacy rights?</h4>
                  <ul>
                    <li>
                      <strong>Withdrawing consent:</strong> You can withdraw
                      your consent at any time by contacting us.
                    </li>
                    <li>
                      <strong>Opting out of marketing:</strong> You can
                      unsubscribe via email links, SMS replies, or by contacting
                      us.
                    </li>
                    <li>
                      <strong>Account Information:</strong> You can update or
                      delete your account information by logging in or
                      contacting us. Some data may be retained for legal or
                      security purposes.
                    </li>
                    <li>
                      <strong>Cookies and similar technologies:</strong> You can
                      manage cookies in your browser settings. See our Cookie
                      Notice for details.
                    </li>
                  </ul>
                </li>

                <li>
                  <h4>Controls for do-not-track features</h4>
                  <p>
                    Currently, we do not respond to browser DNT signals due to
                    the absence of a uniform standard. If one is adopted, we
                    will update this notice.
                  </p>
                </li>

                <li>
                  <h4>Do we make updates to this notice?</h4>
                  <p>
                    We may update this notice occasionally. Changes will be
                    indicated by a “Revised” date and will take effect when
                    posted. For material changes, we may notify you directly or
                    via a prominent notice.
                  </p>
                </li>

                <li>
                  <h4>How can you contact us about this notice?</h4>
                  <p>
                    Email:{" "}
                    <a href="mailto:hello@krent.space">hello@krent.space</a>
                  </p>
                  <p>Phone: 09118008008</p>
                  <p>
                    Address:
                    <br />
                    H3, Bayview Estate
                    <br />
                    Ikate, Lekki
                    <br />
                    Lagos State, Nigeria
                  </p>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>
    </BaseLayout>
  );
};

export default Privacy;
